import { useMemo } from 'react';

import { type UUID } from '~/types/common';
import { type RouteExport } from '~/types/router.type';

import AuthService from '~/services/auth.service';

import { PermissionsObject } from '~/models/masterdata/Permissions';
import { UserObject } from '~/models/masterdata/User';

import { intersection } from '@vestigas/utils';

import {
  ANBS_COMPANIES,
  BAYWA_SCREEN_USER,
  DEMO_ALLOWED_COMPANY_ACCOUNTS,
  DLN_DATE_COMPANY_ACCOUNTS,
  SCHICKER_COMPANY_ACCOUNTS,
  STRABAG_COMPANY_ACCOUNTS,
  STRABAG_SUPPORT_USERS,
  SUPPLIER_OVERVIEW_USER,
  USERS_WITH_CUSTOM_FIELDS,
  VESTIGAS_ACCOUNTS,
} from './constants';
import { useQueryUserData } from './useQueryUserData';

export const useGetUserUtils = () => {
  const { data: currentUser } = useQueryUserData(true);
  const userPermissions = currentUser?.userPermissions ?? [];
  const isVestigasSupport = Boolean(currentUser?.isVestigasSupport);
  const companyId = currentUser?.companyInfo?.id;

  const methods = useMemo(() => {
    return {
      ANBS_COMPANIES,
      BAYWA_SCREEN_USER,
      DEMO_ALLOWED_COMPANY_ACCOUNTS,
      DLN_DATE_COMPANY_ACCOUNTS,
      SCHICKER_COMPANY_ACCOUNTS,
      STRABAG_COMPANY_ACCOUNTS,
      STRABAG_SUPPORT_USERS,
      SUPPLIER_OVERVIEW_USER,
      USERS_WITH_CUSTOM_FIELDS,
      VESTIGAS_ACCOUNTS,

      isVestigasSupportAccount() {
        return isVestigasSupport;
      },
      isVestigasAccount() {
        const accessToken = AuthService.getAccessToken();
        if (!accessToken) {
          return false;
        }

        const accessTokenPayload = AuthService.getJwtPayload(accessToken);

        if (!accessTokenPayload) {
          return false;
        }

        return (
          this.isVestigasSupportAccount() ||
          VESTIGAS_ACCOUNTS.includes(accessTokenPayload.preferred_username) ||
          accessTokenPayload.preferred_username
            ?.toLowerCase()
            ?.includes('support') ||
          accessTokenPayload.email?.toLowerCase()?.includes('support')
        );
      },
      isVestigasAdminAccount() {
        const accessToken = AuthService.getAccessToken();

        if (!accessToken) {
          return false;
        }

        const accessTokenPayload = AuthService.getJwtPayload(accessToken);

        if (!accessTokenPayload) {
          return false;
        }

        return accessTokenPayload.preferred_username === 'vestigas-admin';
      },
      isSchickerAccount(companyAccountId: UUID) {
        return this.SCHICKER_COMPANY_ACCOUNTS.includes(companyAccountId);
      },
      // Should we render the DLN date as full date time instead of just the date?
      showFullDlnDateDateTime(companyAccountId: UUID) {
        return this.DLN_DATE_COMPANY_ACCOUNTS.includes(companyAccountId);
      },

      isStrabagAccount(companyAccountId: UUID) {
        return this.STRABAG_COMPANY_ACCOUNTS.includes(companyAccountId);
      },

      isANBSAccount(companyId: UUID) {
        return this.ANBS_COMPANIES.includes(companyId);
      },

      // user can see permitted/mapped sites and cost centers
      isPermittedSiteAllowedUser() {
        return this.isVestigasAccount();
      },

      isDelayedSignatureHomeScreenAllowedUser(
        userId: UUID,
        featureFlags: Record<string, boolean>,
      ) {
        if (this.STRABAG_SUPPORT_USERS.includes(userId)) {
          return false;
        }

        if (featureFlags?.disableInvoices) {
          return false;
        }

        if (this.isVestigasAccount()) {
          return true;
        }
      },

      isUserReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_USER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },
      isUserWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_USER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isUserCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_USER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isSiteReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_SITE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isSiteWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_SITE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isSiteCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_SITE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCostCenterReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_COST_CENTER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCostCenterWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_COST_CENTER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCostCenterCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_COST_CENTER.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      // contract.create has no PERMISSIONS_AVAILABLE entry yet (read-only wiring for
      // OEO-750), so it is checked directly against the raw backend permission string.
      isContractCreateAllowedUser() {
        return userPermissions.includes('contract.create');
      },

      isVehicleReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_VEHICLE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isVehicleWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_VEHICLE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isVehicleCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_VEHICLE.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCompanyReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_COMPANY.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCompanyWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_COMPANY.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isCompanyCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_COMPANY.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isOrganisationalGroupReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_ORGANISATIONAL_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isOrganisationalGroupWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_ORGANISATIONAL_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isOrganisationalGroupCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_ORGANISATIONAL_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isOrganisationalGroupDeleteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.DELETE_ORGANISATIONAL_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isUserGroupReadAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.READ_USER_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isUserGroupWriteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.WRITE_USER_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isUserGroupCreateAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.CREATE_USER_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isUserGroupDeleteAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.DELETE_USER_GROUP.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      isPermissionGrantAllowedUser() {
        const requiredPermissions =
          PermissionsObject.PERMISSION.GRANT_PERMISSION.BACKEND;

        return intersection(userPermissions, requiredPermissions).length > 0;
      },

      hasAnyAccessGrantPermission() {
        // Collect all BACKEND permissions from PERMISSIONS_AVAILABLE that end with '.grant'
        const allGrantPermissions = Object.values(PermissionsObject.PERMISSION)
          .flatMap((permission) => permission.BACKEND)
          .filter((permission) => permission.endsWith('.grant'));

        return intersection(userPermissions, allGrantPermissions).length > 0;
      },

      isExternalSupplierOverviewUser() {
        return this.SUPPLIER_OVERVIEW_USER.includes(
          AuthService.getUserIdFromAccessToken(),
        );
      },

      userIsAuthorizedForFeatureFlag(featureFlag: string) {
        if (currentUser?.featureFlags?.[featureFlag]) {
          return true;
        }

        return false;
      },
      userIsAuthorizedForPage(
        route: RouteExport,
        userPermissions: string[],
        featureFlags: Record<string, boolean>,
      ) {
        if (route.vestigasSupportAccess && !this.isVestigasAccount()) {
          return false;
        }

        let authorized = false;

        const requiredPermissions = UserObject.getRequiredPermissions(route);

        if (requiredPermissions) {
          for (const userPermission of userPermissions) {
            for (const routePermission of requiredPermissions) {
              if (userPermission.includes(routePermission)) {
                authorized = true;
              }
            }
          }
        } else {
          authorized = true;
        }

        const { disablingFeatureFlags, requiredFeatureFlags } =
          UserObject.getFeatureFlag(route);

        // All required feature flags must be enabled
        if (
          Boolean(requiredFeatureFlags?.length) &&
          !requiredFeatureFlags.every((flag) => featureFlags?.[flag])
        ) {
          return false;
        }

        // Any disabling feature flag being enabled denies access
        if (
          Boolean(disablingFeatureFlags?.length) &&
          disablingFeatureFlags.some((flag) => featureFlags?.[flag])
        ) {
          return false;
        }

        return authorized;
      },
    };
  }, [
    companyId,
    currentUser?.featureFlags,
    isVestigasSupport,
    userPermissions,
  ]);

  return {
    UserUtils: methods,
  };
};
